import React, { useState, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import {
  FiSearch,
  FiDownload,
  FiEye,
  FiCheck,
  FiX,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
  FiRotateCcw,
  FiCalendar,
  FiPackage,
  FiAlertCircle,
  FiKey,
} from "react-icons/fi";

const StudentDirectory = ({
  students = [],
  loading = false,
  pagination = { total: 0, totalPages: 1, currentPage: 1, limit: 10 },
  searchTerm = "",
  filterPayment = "all",
  filterAttendance = "all",
  onSearch,
  onPaymentFilterChange,
  onAttendanceFilterChange,
  onPageChange,
  onResetFilters,
  onExportCSV,
  onViewStudent,
  onTogglePayment,
  onDeleteStudent,
  onRegeneratePassword,
  regenLoadingId,
}) => {
  // Local search input state
  const [localSearch, setLocalSearch] = useState(searchTerm);

  // Sync local search when prop resets
  useEffect(() => {
    setLocalSearch(searchTerm);
  }, [searchTerm]);

  const totalStudents = pagination?.total || 0;
  const totalPages = pagination?.totalPages || 1;
  const currentPage = pagination?.currentPage || 1;
  const limit = pagination?.limit || 10;

  const startRecord =
    totalStudents === 0 ? 0 : (currentPage - 1) * limit + 1;
  const endRecord = Math.min(currentPage * limit, totalStudents);

  // Check if any filter is active
  const isFiltered =
    searchTerm !== "" || filterPayment !== "all" || filterAttendance !== "all";

  // Form search submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (onSearch) {
      onSearch(localSearch);
    }
  };

  const handleClearSearch = () => {
    setLocalSearch("");
    if (onSearch) {
      onSearch("");
    }
  };

  // Helper for generating pagination buttons with ellipsis
  const getPaginationPages = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 2) {
        pages.push(1, 2, 3, "...", totalPages);
      } else if (currentPage >= totalPages - 1) {
        pages.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, "...", currentPage, "...", totalPages);
      }
    }
    return pages;
  };

  return (
    <div className="admin-card">
      {/* Search & Filter Header Toolbar */}
      <div className="admin-toolbar">
        {/* Left: Backend Search Form with Search Button */}
        <form onSubmit={handleSearchSubmit} className="admin-search-form">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              height: "38px",
              border: "1px solid #cbd5e1",
              borderRadius: "8px",
              padding: "0 10px",
              backgroundColor: "#f8fafc",
              boxSizing: "border-box",
            }}
          >
            <FiSearch color="#94a3b8" size={15} style={{ flexShrink: 0 }} />
            <input
              type="text"
              placeholder="Search name, reg no, email, branch..."
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              style={{
                width: "100%",
                padding: "0 8px",
                border: "none",
                outline: "none",
                backgroundColor: "transparent",
                fontSize: "13px",
                color: "#1e293b",
              }}
            />
            {localSearch && (
              <button
                type="button"
                onClick={handleClearSearch}
                style={{
                  background: "none",
                  border: "none",
                  color: "#94a3b8",
                  cursor: "pointer",
                  padding: "2px",
                  fontSize: "13px",
                  display: "flex",
                  alignItems: "center",
                  flexShrink: 0,
                }}
                title="Clear Search"
              >
                <FiX />
              </button>
            )}
          </div>
          <button
            type="submit"
            style={{
              height: "38px",
              padding: "0 14px",
              backgroundColor: "#e06d15",
              color: "#fff",
              fontWeight: 600,
              fontSize: "13px",
              borderRadius: "8px",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              whiteSpace: "nowrap",
              boxShadow: "0 2px 6px rgba(224, 109, 21, 0.25)",
              boxSizing: "border-box",
            }}
          >
            <FiSearch size={13} /> Search
          </button>
        </form>

        {/* Right: Filters & Export Actions */}
        <div className="admin-filters-group">
          {/* Dues Status Filter */}
          <select
            value={filterPayment}
            onChange={(e) =>
              onPaymentFilterChange && onPaymentFilterChange(e.target.value)
            }
            style={{
              height: "38px",
              padding: "0 10px",
              borderRadius: "8px",
              border: "1px solid #cbd5e1",
              fontSize: "13px",
              backgroundColor: "#f8fafc",
              fontWeight: 500,
              color: "#334155",
              cursor: "pointer",
              outline: "none",
              boxSizing: "border-box",
            }}
          >
            <option value="all">All Dues Status</option>
            <option value="cleared">Cleared (No Dues)</option>
            <option value="pending">Pending Dues</option>
          </select>

          {/* Attendance Filter */}
          <select
            value={filterAttendance}
            onChange={(e) =>
              onAttendanceFilterChange && onAttendanceFilterChange(e.target.value)
            }
            style={{
              height: "38px",
              padding: "0 10px",
              borderRadius: "8px",
              border: "1px solid #cbd5e1",
              fontSize: "13px",
              backgroundColor: "#f8fafc",
              fontWeight: 500,
              color: "#334155",
              cursor: "pointer",
              outline: "none",
              boxSizing: "border-box",
            }}
          >
            <option value="all">All Attendance</option>
            <option value="inPerson">In Person</option>
            <option value="courier">Courier</option>
            <option value="notRegistered">Not Registered</option>
          </select>

          {/* Reset Filters Button */}
          {isFiltered && (
            <button
              onClick={onResetFilters}
              style={{
                height: "38px",
                padding: "0 12px",
                backgroundColor: "#f1f5f9",
                color: "#64748b",
                fontWeight: 600,
                fontSize: "13px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                whiteSpace: "nowrap",
                boxSizing: "border-box",
              }}
              title="Reset all search and filters"
            >
              <FiRotateCcw size={13} /> Reset
            </button>
          )}

          {/* Export CSV Button */}
          <button
            onClick={onExportCSV}
            className="admin-btn-export"
            style={{
              height: "38px",
              padding: "0 14px",
              backgroundColor: "#059669",
              color: "#fff",
              fontWeight: 600,
              fontSize: "13px",
              borderRadius: "8px",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              whiteSpace: "nowrap",
              boxShadow: "0 2px 6px rgba(5, 150, 105, 0.2)",
              boxSizing: "border-box",
            }}
          >
            <FiDownload size={14} /> Export CSV ({totalStudents})
          </button>
        </div>
      </div>

      {/* Table Section */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px" }}>
          <Spinner animation="border" variant="warning" />
          <p style={{ marginTop: "12px", color: "#64748b", fontSize: "14px" }}>
            Loading graduates directory...
          </p>
        </div>
      ) : students.length === 0 ? (
        <div
          style={{
            padding: "50px 20px",
            textAlign: "center",
            color: "#64748b",
            background: "#f8fafc",
            borderRadius: "10px",
            border: "1px dashed #cbd5e1",
          }}
        >
          <FiAlertCircle
            size={32}
            color="#94a3b8"
            style={{ marginBottom: "8px" }}
          />
          <div style={{ fontSize: "16px", fontWeight: 600, color: "#334155" }}>
            No students found
          </div>
          <div style={{ fontSize: "13px", color: "#64748b", marginTop: "4px" }}>
            {isFiltered
              ? "No records matched your search filters. Try resetting the filters."
              : "No students registered yet in the system."}
          </div>
          {isFiltered && (
            <button
              onClick={onResetFilters}
              style={{
                marginTop: "14px",
                padding: "7px 16px",
                backgroundColor: "#e2e8f0",
                color: "#1e293b",
                border: "none",
                borderRadius: "6px",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Reset Filters
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr
                  style={{
                    textAlign: "left",
                    borderBottom: "2px solid #e2e8f0",
                    color: "#475569",
                    backgroundColor: "#f8fafc",
                  }}
                >
                  <th style={{ padding: "12px 14px", width: "50px" }}>#</th>
                  <th style={{ padding: "12px 14px" }}>Reg No</th>
                  <th style={{ padding: "12px 14px" }}>Student Name</th>
                  <th style={{ padding: "12px 14px" }}>Programme & School</th>
                  <th style={{ padding: "12px 14px" }}>Batch / GPA</th>
                  <th style={{ padding: "12px 14px" }}>Attendance</th>
                  <th style={{ padding: "12px 14px" }}>Dues Status</th>
                  <th style={{ padding: "12px 14px", textAlign: "right" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {students.map((s, index) => {
                  const serialNo = (currentPage - 1) * limit + index + 1;
                  return (
                    <tr
                      key={s._id || s.reg_no}
                      style={{
                        borderBottom: "1px solid #f1f5f9",
                        transition: "background 0.15s ease",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = "#f8fafc")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = "transparent")
                      }
                    >
                      {/* S.No */}
                      <td
                        style={{
                          padding: "14px 14px",
                          fontWeight: 600,
                          color: "#94a3b8",
                          fontSize: "13px",
                        }}
                      >
                        {serialNo}
                      </td>

                      {/* Reg No */}
                      <td
                        style={{
                          padding: "14px 14px",
                          fontWeight: 700,
                          color: "#0f172a",
                          fontFamily: "monospace",
                          fontSize: "13.5px",
                        }}
                      >
                        {s.reg_no}
                      </td>

                      {/* Student Name & Email */}
                      <td style={{ padding: "14px 14px" }}>
                        <div style={{ fontWeight: 600, color: "#0f172a" }}>
                          {s.student_name}
                        </div>
                        <div style={{ fontSize: "12px", color: "#64748b" }}>
                          {s.email}
                        </div>
                        {(s.account_number || s.bank_name || s.address) && (
                          <div style={{ marginTop: "4px" }}>
                            <span
                              style={{
                                fontSize: "10.5px",
                                fontWeight: 600,
                                color: "#047857",
                                backgroundColor: "#ecfdf5",
                                padding: "1px 6px",
                                borderRadius: "4px",
                                border: "1px solid #a7f3d0",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "3px",
                              }}
                            >
                              ✓ Details Submitted
                            </span>
                          </div>
                        )}
                      </td>

                      {/* Programme & School */}
                      <td style={{ padding: "14px 14px" }}>
                        <div style={{ fontWeight: 600, color: "#334155" }}>
                          {s.programme}
                        </div>
                        <div style={{ fontSize: "12px", color: "#64748b" }}>
                          {s.school}
                        </div>
                      </td>

                      {/* Batch & CGPA */}
                      <td style={{ padding: "14px 14px" }}>
                        <div style={{ fontSize: "13px", color: "#334155" }}>
                          {s.batch}
                        </div>
                        <div style={{ marginTop: "2px" }}>
                          <span
                            style={{
                              backgroundColor: "#f1f5f9",
                              color: "#0f172a",
                              fontWeight: 700,
                              fontSize: "11px",
                              padding: "2px 6px",
                              borderRadius: "4px",
                              border: "1px solid #e2e8f0",
                            }}
                          >
                            CGPA: {s.cgpa || "N/A"}
                          </span>
                        </div>
                      </td>

                      {/* Attendance */}
                      <td style={{ padding: "14px 14px" }}>
                        {(s.day?.toLowerCase() === "courier" || s.attending === "courier") ? (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "5px",
                              padding: "4px 10px",
                              fontSize: "12px",
                              fontWeight: 600,
                              borderRadius: "20px",
                              backgroundColor: "#eff6ff",
                              color: "#1d4ed8",
                              border: "1px solid #bfdbfe",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <FiPackage size={13} /> Courier
                          </span>
                        ) : (s.day || s.attending === "inPerson") ? (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "5px",
                              padding: "4px 10px",
                              fontSize: "12px",
                              fontWeight: 600,
                              borderRadius: "20px",
                              backgroundColor: "#ecfdf5",
                              color: "#047857",
                              border: "1px solid #a7f3d0",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <FiCalendar size={13} /> In Person ({s.day}) +{s.companions || 0}
                          </span>
                        ) : (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              padding: "4px 10px",
                              fontSize: "12px",
                              fontWeight: 600,
                              borderRadius: "20px",
                              backgroundColor: "#f8fafc",
                              color: "#64748b",
                              border: "1px solid #e2e8f0",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <span
                              style={{
                                width: "6px",
                                height: "6px",
                                borderRadius: "50%",
                                backgroundColor: "#94a3b8",
                                display: "inline-block",
                              }}
                            />
                            Not Registered
                          </span>
                        )}
                      </td>

                      {/* Dues Status */}
                      <td style={{ padding: "14px 14px" }}>
                        {s.has_due ? (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "5px",
                              padding: "4px 10px",
                              fontSize: "12px",
                              fontWeight: 600,
                              borderRadius: "20px",
                              backgroundColor: "#fef2f2",
                              color: "#b91c1c",
                              border: "1px solid #fecaca",
                              whiteSpace: "nowrap",
                            }}
                            title={`${s.pending_dues_count || 1} pending due(s)`}
                          >
                            <FiAlertCircle size={13} /> Pending Due (₹{s.total_due_amount || 0})
                          </span>
                        ) : (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "5px",
                              padding: "4px 10px",
                              fontSize: "12px",
                              fontWeight: 600,
                              borderRadius: "20px",
                              backgroundColor: "#ecfdf5",
                              color: "#047857",
                              border: "1px solid #a7f3d0",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <FiCheck size={13} /> Cleared (No Dues)
                          </span>
                        )}
                      </td>

                      {/* Actions */}
                      <td
                        style={{
                          padding: "14px 14px",
                          textAlign: "right",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <div
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: "8px",
                          }}
                        >
                          {/* View Icon Button */}
                          <button
                            type="button"
                            onClick={() => onViewStudent(s)}
                            title="View Student Details"
                            style={{
                              width: "34px",
                              height: "34px",
                              padding: 0,
                              borderRadius: "8px",
                              border: "1px solid #bfdbfe",
                              backgroundColor: "#eff6ff",
                              color: "#2563eb",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              transition: "all 0.15s ease",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = "#2563eb";
                              e.currentTarget.style.color = "#ffffff";
                              e.currentTarget.style.borderColor = "#2563eb";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = "#eff6ff";
                              e.currentTarget.style.color = "#2563eb";
                              e.currentTarget.style.borderColor = "#bfdbfe";
                            }}
                          >
                            <FiEye size={16} />
                          </button>

                          {/* Regenerate & Resend Password Button - Only visible for students who already have password generated */}
                          {(s.has_password || s.password) && (
                            <button
                              type="button"
                              onClick={() =>
                                onRegeneratePassword && onRegeneratePassword(s)
                              }
                              disabled={
                                regenLoadingId === s._id ||
                                regenLoadingId === s.reg_no
                              }
                              title="Regenerate & Resend Password to Student"
                              style={{
                                width: "34px",
                                height: "34px",
                                padding: 0,
                                borderRadius: "8px",
                                border: "1px solid #fed7aa",
                                backgroundColor: "#fff7ed",
                                color: "#ea580c",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor:
                                  regenLoadingId === s._id ||
                                  regenLoadingId === s.reg_no
                                    ? "not-allowed"
                                    : "pointer",
                                transition: "all 0.15s ease",
                                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                              }}
                              onMouseEnter={(e) => {
                                if (
                                  regenLoadingId !== s._id &&
                                  regenLoadingId !== s.reg_no
                                ) {
                                  e.currentTarget.style.backgroundColor =
                                    "#ea580c";
                                  e.currentTarget.style.color = "#ffffff";
                                  e.currentTarget.style.borderColor = "#ea580c";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (
                                  regenLoadingId !== s._id &&
                                  regenLoadingId !== s.reg_no
                                ) {
                                  e.currentTarget.style.backgroundColor =
                                    "#fff7ed";
                                  e.currentTarget.style.color = "#ea580c";
                                  e.currentTarget.style.borderColor = "#fed7aa";
                                }
                              }}
                            >
                              {regenLoadingId === s._id ||
                              regenLoadingId === s.reg_no ? (
                                <Spinner
                                  animation="border"
                                  size="sm"
                                  style={{
                                    width: "14px",
                                    height: "14px",
                                    borderWidth: "2px",
                                  }}
                                />
                              ) : (
                                <FiKey size={15} />
                              )}
                            </button>
                          )}

                          {/* Delete Icon Button */}
                          <button
                            type="button"
                            onClick={() => onDeleteStudent(s.reg_no)}
                            title="Delete Student"
                            style={{
                              width: "34px",
                              height: "34px",
                              padding: 0,
                              borderRadius: "8px",
                              border: "1px solid #fecaca",
                              backgroundColor: "#fef2f2",
                              color: "#dc2626",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              transition: "all 0.15s ease",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = "#dc2626";
                              e.currentTarget.style.color = "#ffffff";
                              e.currentTarget.style.borderColor = "#dc2626";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = "#fef2f2";
                              e.currentTarget.style.color = "#dc2626";
                              e.currentTarget.style.borderColor = "#fecaca";
                            }}
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          <div className="admin-pagination-wrapper">
            {/* Range info */}
            <div className="admin-pagination-info">
              <span>Showing</span>
              <span
                style={{
                  fontWeight: 700,
                  color: "#0f172a",
                  backgroundColor: "#f8fafc",
                  padding: "2px 8px",
                  borderRadius: "6px",
                  border: "1px solid #e2e8f0",
                }}
              >
                {startRecord} – {endRecord}
              </span>
              <span>of</span>
              <span style={{ fontWeight: 700, color: "#0f172a" }}>
                {totalStudents}
              </span>
              <span>graduates</span>
            </div>

            {/* Modern Pagination Controls */}
            <div className="admin-pagination-controls">
              {/* First Page */}
              <button
                type="button"
                className="admin-page-jump-btn"
                onClick={() => onPageChange && onPageChange(1)}
                disabled={currentPage === 1}
                style={{
                  height: "36px",
                  width: "36px",
                  padding: 0,
                  borderRadius: "8px",
                  border:
                    currentPage === 1
                      ? "1px solid #f1f5f9"
                      : "1px solid #e2e8f0",
                  backgroundColor: currentPage === 1 ? "#f8fafc" : "#ffffff",
                  color: currentPage === 1 ? "#cbd5e1" : "#475569",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "15px",
                  transition: "all 0.15s ease",
                  boxShadow:
                    currentPage === 1
                      ? "none"
                      : "0 1px 2px rgba(0,0,0,0.04)",
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== 1) {
                    e.currentTarget.style.backgroundColor = "#f1f5f9";
                    e.currentTarget.style.borderColor = "#cbd5e1";
                    e.currentTarget.style.color = "#0f172a";
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentPage !== 1) {
                    e.currentTarget.style.backgroundColor = "#ffffff";
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.color = "#475569";
                  }
                }}
                title="First Page"
              >
                <FiChevronsLeft size={16} />
              </button>

              {/* Prev Page */}
              <button
                type="button"
                className="admin-page-nav-btn"
                onClick={() =>
                  onPageChange && onPageChange(Math.max(1, currentPage - 1))
                }
                disabled={currentPage === 1}
                style={{
                  height: "36px",
                  padding: "0 12px",
                  borderRadius: "8px",
                  border:
                    currentPage === 1
                      ? "1px solid #f1f5f9"
                      : "1px solid #e2e8f0",
                  backgroundColor: currentPage === 1 ? "#f8fafc" : "#ffffff",
                  color: currentPage === 1 ? "#cbd5e1" : "#475569",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  fontSize: "13px",
                  fontWeight: 600,
                  transition: "all 0.15s ease",
                  boxShadow:
                    currentPage === 1
                      ? "none"
                      : "0 1px 2px rgba(0,0,0,0.04)",
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== 1) {
                    e.currentTarget.style.backgroundColor = "#f1f5f9";
                    e.currentTarget.style.borderColor = "#cbd5e1";
                    e.currentTarget.style.color = "#0f172a";
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentPage !== 1) {
                    e.currentTarget.style.backgroundColor = "#ffffff";
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.color = "#475569";
                  }
                }}
                title="Previous Page"
              >
                <FiChevronLeft size={16} /> Prev
              </button>

              {/* Numeric Page Buttons */}
              {getPaginationPages().map((page, idx) => {
                if (page === "...") {
                  return (
                    <span
                      key={`ellipsis-${idx}`}
                      style={{
                        height: "34px",
                        minWidth: "22px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#94a3b8",
                        fontSize: "13px",
                        fontWeight: 600,
                      }}
                    >
                      ···
                    </span>
                  );
                }

                const isActive = currentPage === page;
                return (
                  <button
                    key={`page-${page}`}
                    type="button"
                    className="admin-page-num-btn"
                    onClick={() => onPageChange && onPageChange(page)}
                    style={{
                      height: "36px",
                      minWidth: "36px",
                      padding: "0 8px",
                      borderRadius: "8px",
                      border: isActive
                        ? "1px solid #ea580c"
                        : "1px solid #e2e8f0",
                      background: isActive
                        ? "linear-gradient(135deg, #ea580c 0%, #c2410c 100%)"
                        : "#ffffff",
                      color: isActive ? "#ffffff" : "#334155",
                      fontWeight: isActive ? 700 : 500,
                      cursor: "pointer",
                      fontSize: "13.5px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.15s ease",
                      boxShadow: isActive
                        ? "0 2px 6px rgba(234, 88, 12, 0.35)"
                        : "0 1px 2px rgba(0,0,0,0.04)",
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = "#f1f5f9";
                        e.currentTarget.style.borderColor = "#cbd5e1";
                        e.currentTarget.style.color = "#0f172a";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = "#ffffff";
                        e.currentTarget.style.borderColor = "#e2e8f0";
                        e.currentTarget.style.color = "#334155";
                      }
                    }}
                  >
                    {page}
                  </button>
                );
              })}

              {/* Next Page */}
              <button
                type="button"
                className="admin-page-nav-btn"
                onClick={() =>
                  onPageChange &&
                  onPageChange(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
                style={{
                  height: "36px",
                  padding: "0 12px",
                  borderRadius: "8px",
                  border:
                    currentPage === totalPages
                      ? "1px solid #f1f5f9"
                      : "1px solid #e2e8f0",
                  backgroundColor:
                    currentPage === totalPages ? "#f8fafc" : "#ffffff",
                  color: currentPage === totalPages ? "#cbd5e1" : "#475569",
                  cursor:
                    currentPage === totalPages ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  fontSize: "13px",
                  fontWeight: 600,
                  transition: "all 0.15s ease",
                  boxShadow:
                    currentPage === totalPages
                      ? "none"
                      : "0 1px 2px rgba(0,0,0,0.04)",
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== totalPages) {
                    e.currentTarget.style.backgroundColor = "#f1f5f9";
                    e.currentTarget.style.borderColor = "#cbd5e1";
                    e.currentTarget.style.color = "#0f172a";
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentPage !== totalPages) {
                    e.currentTarget.style.backgroundColor = "#ffffff";
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.color = "#475569";
                  }
                }}
                title="Next Page"
              >
                Next <FiChevronRight size={16} />
              </button>

              {/* Last Page */}
              <button
                type="button"
                className="admin-page-jump-btn"
                onClick={() => onPageChange && onPageChange(totalPages)}
                disabled={currentPage === totalPages}
                style={{
                  height: "36px",
                  width: "36px",
                  padding: 0,
                  borderRadius: "8px",
                  border:
                    currentPage === totalPages
                      ? "1px solid #f1f5f9"
                      : "1px solid #e2e8f0",
                  backgroundColor:
                    currentPage === totalPages ? "#f8fafc" : "#ffffff",
                  color: currentPage === totalPages ? "#cbd5e1" : "#475569",
                  cursor:
                    currentPage === totalPages ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "15px",
                  transition: "all 0.15s ease",
                  boxShadow:
                    currentPage === totalPages
                      ? "none"
                      : "0 1px 2px rgba(0,0,0,0.04)",
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== totalPages) {
                    e.currentTarget.style.backgroundColor = "#f1f5f9";
                    e.currentTarget.style.borderColor = "#cbd5e1";
                    e.currentTarget.style.color = "#0f172a";
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentPage !== totalPages) {
                    e.currentTarget.style.backgroundColor = "#ffffff";
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.color = "#475569";
                  }
                }}
                title="Last Page"
              >
                <FiChevronsRight size={16} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default StudentDirectory;
